import { useEffect, useMemo, useState } from "react";
import { api, fmtAUD, fmtDate, formatApiError } from "@/lib/api";
import { Target, Plus, Edit2, Trash2, TrendingUp, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function GoalsAdmin() {
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([api.get("/users"), api.get("/goals")]);
      setUsers(u.data);
      setGoals(g.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredGoals = useMemo(
    () => (filterUser ? goals.filter((g) => g.user_id === filterUser) : goals),
    [goals, filterUser]
  );

  const summaryByUser = useMemo(() => {
    const map = {};
    for (const g of goals) {
      const k = g.user_id;
      if (!map[k]) map[k] = { user_id: k, user_name: g.user_name, target: 0, achieved: 0, count: 0 };
      map[k].target += Number(g.target_amount || 0);
      map[k].achieved += Number(g.achieved_amount || 0);
      map[k].count += 1;
    }
    return Object.values(map).sort((a, b) => b.target - a.target);
  }, [goals]);

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/goals/${confirmDelete.id}`);
      toast.success("Goal removed");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8" data-testid="goals-admin-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyb-label">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Team Goals</h1>
          <p className="text-sm text-slate-500 mt-1">Assign revenue targets and track progress from closed-won opportunities.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="goals-filter-user"
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
          >
            <option value="">All team members</option>
            {users.filter((u) => u.active).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <Button
            data-testid="new-goal-btn"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="bg-[#1B4F8B] hover:bg-[#163e6e]"
          >
            <Plus className="w-4 h-4 mr-1" /> New Goal
          </Button>
        </div>
      </div>

      {/* Per-user summary */}
      {!loading && summaryByUser.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="goals-summary-grid">
          {summaryByUser.map((s) => {
            const pct = s.target > 0 ? Math.min(100, (s.achieved / s.target) * 100) : 0;
            return (
              <div key={s.user_id} className="eyb-card p-5" data-testid={`goals-summary-${s.user_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="eyb-label">Team member</div>
                    <div className="font-heading text-base font-semibold text-slate-900 mt-1">{s.user_name || "—"}</div>
                    <div className="text-xs text-slate-500">{s.count} goal{s.count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#29ABE2] to-[#1B4F8B] text-white flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="font-semibold text-slate-900">{fmtAUD(s.achieved)}</span>
                    <span className="text-slate-500">of {fmtAUD(s.target)}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-[#29ABE2]"} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{pct.toFixed(1)}% of total target</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Goals table */}
      <div className="eyb-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
              <th className="px-4 py-3 font-semibold">Team Member</th>
              <th className="px-4 py-3 font-semibold">Label</th>
              <th className="px-4 py-3 font-semibold">Period</th>
              <th className="px-4 py-3 font-semibold">Target</th>
              <th className="px-4 py-3 font-semibold">Closed Won</th>
              <th className="px-4 py-3 font-semibold">Progress</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
            ) : filteredGoals.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">No goals assigned yet. Click <span className="font-semibold">New Goal</span> to set a target.</td></tr>
            ) : (
              filteredGoals.map((g) => {
                const pct = Math.min(100, Number(g.percent) || 0);
                return (
                  <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`goal-row-${g.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{g.user_name || "—"}</div>
                      <div className="text-[11px] text-slate-500">{g.user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{g.label || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(g.period_start)} → {fmtDate(g.period_end)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{fmtAUD(g.target_amount)}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-semibold">{fmtAUD(g.achieved_amount)}</div>
                      <div className="text-[11px] text-slate-500">{g.won_count} deal{g.won_count === 1 ? "" : "s"}</div>
                    </td>
                    <td className="px-4 py-3 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full ${Number(g.percent) >= 100 ? "bg-emerald-500" : "bg-[#29ABE2]"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className={`text-xs font-semibold ${Number(g.percent) >= 100 ? "text-emerald-600" : "text-slate-700"}`}>
                          {Number(g.percent).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`goal-edit-${g.id}`}
                        onClick={() => { setEditing(g); setShowForm(true); }}
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`goal-delete-${g.id}`}
                        onClick={() => setConfirmDelete(g)}
                        className="p-1.5 rounded-md hover:bg-rose-50 text-slate-500 hover:text-rose-600 ml-1"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <GoalFormModal
          users={users}
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-goal">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove goal?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? (
                <>This will remove the {fmtAUD(confirmDelete.target_amount)} target for{" "}
                <span className="font-semibold">{confirmDelete.user_name}</span> ({fmtDate(confirmDelete.period_start)} → {fmtDate(confirmDelete.period_end)}). Closed-won opportunities themselves are not affected.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-goal-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-goal-action" className="bg-rose-600 hover:bg-rose-700">
              Remove goal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GoalFormModal({ users, initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    user_id: initial?.user_id || "",
    target_amount: initial?.target_amount || "",
    period_start: initial?.period_start || "",
    period_end: initial?.period_end || "",
    label: initial?.label || "",
    notes: initial?.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const editing = !!initial;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.target_amount || !form.period_start || !form.period_end) {
      toast.error("Team member, target and period are required");
      return;
    }
    if (Number(form.target_amount) <= 0) {
      toast.error("Target must be greater than 0");
      return;
    }
    if (form.period_end < form.period_start) {
      toast.error("Period end must be on or after period start");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id,
        target_amount: Number(form.target_amount),
        period_start: form.period_start,
        period_end: form.period_end,
        label: form.label || null,
        notes: form.notes || null,
      };
      if (editing) {
        // user_id is fixed once created
        const { user_id, ...patch } = payload;
        await api.patch(`/goals/${initial.id}`, patch);
        toast.success("Goal updated");
      } else {
        await api.post("/goals", payload);
        toast.success("Goal created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="goal-form-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#29ABE2]/10 text-[#187199] flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>{editing ? "Edit goal" : "Set a new goal"}</DialogTitle>
              <DialogDescription>
                Goals are tracked automatically from Closed Won opportunities with a close date inside the period.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="goal-user">Team member</Label>
            <select
              id="goal-user"
              data-testid="goal-user-select"
              value={form.user_id}
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              disabled={editing}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500"
              required
            >
              <option value="">Select a team member…</option>
              {users.filter((u) => u.active).map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role.replace("_", " ")}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="goal-target">Target amount (AUD)</Label>
            <Input
              id="goal-target"
              data-testid="goal-target-input"
              type="number"
              min="1"
              step="1"
              value={form.target_amount}
              onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
              placeholder="e.g. 150000"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="goal-start">Period start</Label>
              <Input
                id="goal-start"
                data-testid="goal-start-input"
                type="date"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="goal-end">Period end</Label>
              <Input
                id="goal-end"
                data-testid="goal-end-input"
                type="date"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="goal-label">Label (optional)</Label>
            <Input
              id="goal-label"
              data-testid="goal-label-input"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder='e.g. "FY26 Q3"'
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="goal-notes">Notes (optional)</Label>
            <textarea
              id="goal-notes"
              data-testid="goal-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="goal-form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#1B4F8B] hover:bg-[#163e6e]" data-testid="goal-form-submit">
              {saving ? "Saving…" : editing ? "Save changes" : "Create goal"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
